import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { validateKey } from "../../utils/encryption";

const EncryptionForm = ({
  encryptKey,
  setEncryptKey,
  onEncrypt,
  isEncrypting,
  hasFile
}) => {
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keyStrength, setKeyStrength] = useState({
    score: 0,
    message: "",
    color: "error"
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleEncryptKeyChange = (e) => {
    const value = e.target.value;
    setEncryptKey(value);
    
    // Validate key
    const validation = validateKey(value);
    if (!validation.isValid) {
      setError(validation.error);
    } else {
      setError("");
    }
    
    // Evaluate key strength
    evaluateKeyStrength(value);
  };

  const evaluateKeyStrength = (key) => {
    if (!key || key.length < 4) {
      setKeyStrength({
        score: 0,
        message: "Very Weak",
        color: "error"
      });
      return;
    }
    
    // Simple key strength evaluation
    let score = 0;
    // Length check
    if (key.length >= 8) score += 1;
    if (key.length >= 12) score += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(key)) score += 1; // Has uppercase
    if (/[0-9]/.test(key)) score += 1; // Has number
    if (/[^A-Za-z0-9]/.test(key)) score += 1; // Has special char
    
    // Set message and color based on score
    let message, color;
    if (score <= 1) {
      message = "Very Weak";
      color = "error";
    } else if (score <= 2) {
      message = "Weak";
      color = "error";
    } else if (score <= 3) {
      message = "Medium";
      color = "warning";
    } else if (score <= 4) {
      message = "Strong";
      color = "success";
    } else {
      message = "Very Strong";
      color = "success";
    }
    
    setKeyStrength({ score, message, color });
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Encryption Key
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Encryption Key"
            variant="outlined"
            fullWidth
            value={encryptKey}
            onChange={handleEncryptKeyChange}
            error={!!error}
            helperText={error || "This key will be used to encrypt and decrypt your file. Keep it safe!"}
            type={showPassword ? "text" : "password"}
            sx={{ mb: 2 }}
            placeholder="Enter a strong encryption key"
            InputProps={{
              startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {encryptKey && !error && (
          <Grid item xs={12}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                mb: 2
              }}
            >
              <Typography variant="body2">
                Key Strength: 
              </Typography>
              <Typography 
                variant="body2"
                color={keyStrength.color + ".main"}
                fontWeight="bold"
              >
                {keyStrength.message}
              </Typography>
            </Box>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={onEncrypt}
            disabled={!hasFile || !!error || !encryptKey || isEncrypting}
            startIcon={isEncrypting ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
          >
            {isEncrypting ? "Encrypting..." : "Encrypt File"}
          </Button>
        </Grid>
        
        {!hasFile && (
          <Grid item xs={12}>
            <Alert severity="info">
              Please upload a file to encrypt first.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default EncryptionForm;